import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { EmptyState } from '@/components/empty-state'
import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import {
  Button,
  Card,
  CardContent,
  FileUpload,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@/components/ui'
import { generateArrayIds } from '@/utils/array.utils'

import { CardDocumento, CardDocumentoSkeleton } from './card-documento'
import { DocumentoStatusUpdater } from './entities/documento-updater.entity'
import { compararDocumentosPorSituacao } from './entities/ordenar-documentos.entity'
import { EnvioDocumentosContext, useEnvioDocumentosContext } from './hooks/use-envio-documentos-context'
import type { TDocumentoAnexado, TEnviarDocumentoParams, TEnvioDocumentosConfig, TTipoDocumento } from './types'

interface EnvioDocumentosProps extends PropsWithChildren {
  config: TEnvioDocumentosConfig
}

const EnvioDocumentosRoot = ({ children, config }: EnvioDocumentosProps) => {
  const [documentosEnviados, setDocumentosEnviados] = useState<TDocumentoAnexado[]>([])
  const [tipoDocumento, setTipoDocumento] = useState<TTipoDocumento | undefined>()
  const [arquivoAnexado, setArquivoAnexado] = useState<File | null>(null)
  const statusUpdater = DocumentoStatusUpdater.create(setDocumentosEnviados)

  const onEnviarDocumento = async (parameters: TEnviarDocumentoParams) =>
    config.enviarDocumentoMutation(parameters, {
      onSuccess: (result) => {
        if (!result?.sucesso) {
          statusUpdater.update(result.documentId, 'error')
          return
        }
        statusUpdater.update(result.documentId, 'success')
      },
      onError: (_, variables) => {
        statusUpdater.update(variables.documentId, 'error')
      },
    })

  const handleEnviarArquivo = async () => {
    if (!arquivoAnexado || !tipoDocumento) return

    const novoDocumento: TDocumentoAnexado = {
      id: crypto.randomUUID(),
      nome: arquivoAnexado.name,
      tamanho: `${(arquivoAnexado.size / (1024 * 1024)).toFixed(1)} MB`,
      tipo: tipoDocumento,
      status: 'uploading',
      arquivo: arquivoAnexado,
    }
    setDocumentosEnviados((prev) => [novoDocumento, ...prev])
    setArquivoAnexado(null)
    setTipoDocumento(undefined)
    onEnviarDocumento({ arquivo: arquivoAnexado, tipo: tipoDocumento, documentId: novoDocumento.id })
  }

  const handleRetry = async (documentId: string) => {
    const document = documentosEnviados.find((doc) => doc.id === documentId)
    if (document?.status !== 'error' || !document?.arquivo) return
    statusUpdater.update(documentId, 'uploading')
    onEnviarDocumento({ arquivo: document.arquivo, tipo: document.tipo, documentId })
  }

  const handleDelete = (documentId: string) => {
    setDocumentosEnviados((prev) => prev.filter((doc) => doc.id !== documentId))
  }

  const contextValue = useMemo(
    () => ({
      config,
      documentosEnviados,
      setDocumentosEnviados,
      tipoDocumento,
      setTipoDocumento,
      arquivoAnexado,
      setArquivoAnexado,
      handleEnviarArquivo,
      handleRetry,
      handleDelete,
    }),
    [config, documentosEnviados, tipoDocumento, arquivoAnexado],
  )

  return (
    <EnvioDocumentosContext.Provider value={contextValue}>
      <Card className="mb-8">
        <CardContent className="p-4 md:p-6">{children}</CardContent>
      </Card>
    </EnvioDocumentosContext.Provider>
  )
}

const EnvioDocumentosHeader = () => {
  const { t } = useTranslation(['proposta', 'common'])
  const navigate = useNavigate()
  const { config } = useEnvioDocumentosContext()

  return (
    <>
      <div className="mb-4">
        <Button onClick={() => navigate(-1)} variant="ghost" className="text-primary h-auto p-0">
          <Icon name="arrow-left" className="h-4 w-4" />
          {config.backButtonText}
        </Button>
      </div>
      <div className="mb-6">
        {config.pessoa.tipo === 'proposta' && (
          <h3 className="text-md mb-1 font-medium text-neutral-500">{config.pessoa.tipoComprador}</h3>
        )}
        <p className="text-md font-semibold text-neutral-700">
          {config.pessoa?.nome || t('common:documentos.pessoa_padrao')}
        </p>
      </div>
      <p className="mb-4 text-sm text-gray-600">{t('proposta:pages.envio_documentos.tipos_arquivos')}</p>
    </>
  )
}

const EnvioDocumentosForm = () => {
  const { t } = useTranslation('proposta')
  const { config, tipoDocumento, setTipoDocumento, arquivoAnexado, setArquivoAnexado, handleEnviarArquivo } =
    useEnvioDocumentosContext()

  return (
    <div className="border-muted flex flex-col gap-4 border p-4 lg:grid lg:grid-cols-8 lg:grid-rows-1">
      <div className="col-span-3">
        <Label className="font-nunito mb-2 block font-medium text-gray-700">
          {t('pages.envio_documentos.inputs.tipo_documento.label')}
        </Label>
        <Select
          key={tipoDocumento}
          value={tipoDocumento}
          onValueChange={(value: TTipoDocumento) => setTipoDocumento(value)}
        >
          <SelectTrigger className="font-nunito w-full text-sm">
            <SelectValue placeholder={t('pages.envio_documentos.inputs.tipo_documento.placholder')} />
          </SelectTrigger>
          <SelectContent>
            {config.documentTypes.map(({ label, value }) => (
              <SelectItem className="font-nunito h-10" key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-4 col-start-4">
        <FileUpload onFileSelect={setArquivoAnexado} selectedFile={arquivoAnexado} />
      </div>
      <div className="col-span-1 col-start-8 content-end md:flex md:justify-end lg:block">
        <Button
          className="flex w-full md:max-w-fit"
          onClick={handleEnviarArquivo}
          disabled={!arquivoAnexado || !tipoDocumento}
        >
          {t('pages.envio_documentos.enviar')}
        </Button>
      </div>
    </div>
  )
}

const EnvioDocumentosList = () => {
  const { t } = useTranslation('proposta')
  const { documentosEnviados, config, setDocumentosEnviados, handleRetry, handleDelete } = useEnvioDocumentosContext()
  const { data, isSuccess, isLoading, isPending } = useQuery(config.recuperarDocumentosQuery)

  useEffect(() => {
    if (isSuccess) {
      setDocumentosEnviados(data)
    }
  }, [isSuccess])

  if (isLoading || isPending) {
    return <ListaDocumentosEnviadosSkeleton />
  }

  return (
    <>
      <Separator className="my-8" />
      <div>
        <h4 className="pb-8 text-lg text-neutral-800">{t('pages.envio_documentos.lista.titulo')}</h4>
        <div className="space-y-4">
          <Render.If
            condition={documentosEnviados}
            fallback={
              <EmptyState
                title={t('pages.envio_documentos.lista.empty.titulo')}
                description={t('pages.envio_documentos.lista.empty.descricao')}
                className="rounded-none border"
              />
            }
          >
            {(_documentos) => (
              <div className="space-y-4">
                {_documentos.toSorted(compararDocumentosPorSituacao).map((documento) => (
                  <CardDocumento
                    key={documento.id}
                    documento={documento}
                    onRetry={({ id }) => handleRetry(id)}
                    onDelete={({ id }) => handleDelete(id)}
                  />
                ))}
              </div>
            )}
          </Render.If>
        </div>
      </div>
    </>
  )
}

const ListaDocumentosEnviadosSkeleton = () => {
  const { t } = useTranslation('proposta')
  return (
    <div>
      <h4 className="pb-8 text-lg text-neutral-800">{t('pages.envio_documentos.lista.titulo')}</h4>
      <div className="space-y-4">
        <div className="space-y-4">
          {generateArrayIds(3).map((id) => (
            <CardDocumentoSkeleton key={id} />
          ))}
        </div>
      </div>
    </div>
  )
}

export const EnvioDocumentos = Object.assign(EnvioDocumentosRoot, {
  Root: EnvioDocumentosRoot,
  Header: EnvioDocumentosHeader,
  Form: EnvioDocumentosForm,
  List: EnvioDocumentosList,
})
